import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OPTION_TYPES,
  type ChecklistQuestion,
  type ChecklistSchema,
  type ChecklistSection,
  type QuestionType,
} from '@/api/checklists.api';

import { ChecklistFormView } from './ChecklistFormView';

type Props = {
  initialTitle?: string;
  initialDescription?: string;
  initialSchema?: ChecklistSchema;
  saving?: boolean;
  saveLabel?: string;
  onSave: (payload: { title: string; description: string; schema: ChecklistSchema }) => void;
  onCancel?: () => void;
};

/** Browser-side id generator — crypto.randomUUID needs a secure context. */
function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The answer types, split into the two groups authors actually think in:
 * a free answer they type themselves, or a pick from a fixed list. Grouping
 * them makes it obvious that a question does NOT have to be Yes/No/N-A.
 * Every type in QUESTION_TYPES must appear in exactly one group.
 */
const TYPE_GROUPS: { key: string; types: QuestionType[] }[] = [
  { key: 'free', types: ['short_text', 'long_text', 'number', 'date'] },
  {
    key: 'choice',
    types: ['yes_no_na', 'yes_no', 'single_choice', 'multi_choice', 'dropdown', 'rating'],
  },
];

/** Yes/No answers are the ones reviewers argue about, so a new question ships
 *  with the comment box on and a No demanding a justification. */
function blankQuestion(): ChecklistQuestion {
  return {
    id: newId(),
    label: '',
    type: 'yes_no_na',
    required: true,
    allowComment: true,
    commentRequiredFor: ['No'],
  };
}

/** Options a comment rule can point at — authored list, or the built-in set. */
function effectiveOptions(question: ChecklistQuestion): string[] {
  if (OPTION_TYPES.includes(question.type)) {
    return (question.options ?? []).map((o) => o.trim()).filter(Boolean);
  }
  if (question.type === 'yes_no') return ['Yes', 'No'];
  if (question.type === 'yes_no_na') return ['Yes', 'No', 'N/A'];
  return [];
}

function blankSection(title: string): ChecklistSection {
  return { id: newId(), title, questions: [blankQuestion()] };
}

/**
 * Google-Forms-style questionnaire builder. Sections hold ordered questions;
 * each question owns its type, required flag and (for choice types) its
 * options. Deliberately no branching/logic — the same schema has to render as
 * a spreadsheet table and a printable PDF, and both break down with it.
 */
export function ChecklistBuilder({
  initialTitle,
  initialDescription,
  initialSchema,
  saving,
  saveLabel,
  onSave,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle ?? '');
  const [description, setDescription] = useState(initialDescription ?? '');
  const [sections, setSections] = useState<ChecklistSection[]>(
    initialSchema?.sections?.length
      ? initialSchema.sections
      : [blankSection(t('checklist.default_section') as string)],
  );
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const questionCount = useMemo(
    () => sections.reduce((sum, s) => sum + s.questions.length, 0),
    [sections],
  );

  const patchSection = (sectionId: string, patch: Partial<ChecklistSection>) =>
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)));

  const patchQuestion = (
    sectionId: string,
    questionId: string,
    patch: Partial<ChecklistQuestion>,
  ) =>
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              questions: s.questions.map((q) => (q.id === questionId ? { ...q, ...patch } : q)),
            }
          : s,
      ),
    );

  const changeType = (sectionId: string, questionId: string, type: QuestionType) => {
    const patch: Partial<ChecklistQuestion> = { type };
    // Option lists only make sense for the authored-choice types; drop them
    // on the way out so a stale list can't reappear if the user flips back.
    if (OPTION_TYPES.includes(type)) {
      const current = sections
        .find((s) => s.id === sectionId)
        ?.questions.find((q) => q.id === questionId);
      patch.options = current?.options?.length ? current.options : ['', ''];
    } else {
      patch.options = undefined;
    }
    patch.max = type === 'rating' ? 5 : undefined;
    // The old rule pointed at the old option list — re-seed it instead of
    // leaving a requirement on an answer the question no longer offers.
    patch.commentRequiredFor =
      type === 'yes_no' || type === 'yes_no_na' ? ['No'] : undefined;
    patchQuestion(sectionId, questionId, patch);
  };

  const moveQuestion = (sectionId: string, index: number, delta: number) =>
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        const next = [...s.questions];
        const target = index + delta;
        if (target < 0 || target >= next.length) return s;
        [next[index], next[target]] = [next[target], next[index]];
        return { ...s, questions: next };
      }),
    );

  const addQuestion = (sectionId: string) =>
    patchSectionQuestions(sectionId, (qs) => [...qs, blankQuestion()]);

  const duplicateQuestion = (sectionId: string, question: ChecklistQuestion) =>
    patchSectionQuestions(sectionId, (qs) => {
      const at = qs.findIndex((q) => q.id === question.id);
      const copy = { ...question, id: newId(), options: question.options ? [...question.options] : undefined };
      const next = [...qs];
      next.splice(at + 1, 0, copy);
      return next;
    });

  const removeQuestion = (sectionId: string, questionId: string) =>
    patchSectionQuestions(sectionId, (qs) => qs.filter((q) => q.id !== questionId));

  function patchSectionQuestions(
    sectionId: string,
    fn: (qs: ChecklistQuestion[]) => ChecklistQuestion[],
  ) {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, questions: fn(s.questions) } : s)),
    );
  }

  const buildSchema = (): ChecklistSchema => ({
    sections: sections.map((s) => ({
      id: s.id,
      title: s.title.trim(),
      ...(s.description?.trim() ? { description: s.description.trim() } : {}),
      questions: s.questions.map((q) => ({
        id: q.id,
        label: q.label.trim(),
        type: q.type,
        required: q.required,
        ...(q.help?.trim() ? { help: q.help.trim() } : {}),
        ...(OPTION_TYPES.includes(q.type)
          ? { options: (q.options ?? []).map((o) => o.trim()).filter(Boolean) }
          : {}),
        ...(q.type === 'rating' ? { max: q.max ?? 5 } : {}),
        ...(q.allowComment
          ? {
              allowComment: true,
              ...(() => {
                const allowed = effectiveOptions(q);
                const req = (q.commentRequiredFor ?? []).filter((o) => allowed.includes(o));
                return req.length ? { commentRequiredFor: req } : {};
              })(),
            }
          : {}),
      })),
    })),
  });

  /** Mirror of the server-side rules so the user isn't bounced by a 400. */
  const validate = (schema: ChecklistSchema): string | null => {
    if (!title.trim()) return t('checklist.err_title') as string;
    if (schema.sections.length === 0) return t('checklist.err_no_section') as string;
    for (const s of schema.sections) {
      if (!s.title) return t('checklist.err_section_title') as string;
      for (const q of s.questions) {
        if (!q.label) return t('checklist.err_question_label') as string;
        if (OPTION_TYPES.includes(q.type) && (q.options ?? []).length === 0) {
          return t('checklist.err_options', { label: q.label }) as string;
        }
      }
    }
    if (schema.sections.every((s) => s.questions.length === 0)) {
      return t('checklist.err_no_question') as string;
    }
    return null;
  };

  const handleSave = () => {
    const schema = buildSchema();
    const problem = validate(schema);
    setError(problem);
    if (problem) return;
    onSave({ title: title.trim(), description: description.trim(), schema });
  };

  const previewSchema = useMemo(() => (preview ? buildSchema() : null), [preview, sections]);

  return (
    <div className="space-y-4">
      {/* ── Form header ─────────────────────────────────────── */}
      <div className="card !p-0 overflow-hidden">
        <div className="h-2 bg-primary" />
        <div className="p-5 space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('checklist.title_placeholder') as string}
            className="w-full border-0 border-b border-border-strong bg-transparent px-0 pb-2 text-[22px] font-semibold text-gray-900 outline-none focus:border-primary"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('checklist.description_placeholder') as string}
            rows={2}
            className="w-full resize-y border-0 border-b border-border bg-transparent px-0 pb-2 text-[13px] text-muted outline-none focus:border-primary"
          />
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-muted">
              {t('checklist.counts', { sections: sections.length, questions: questionCount })}
            </div>
            <button
              type="button"
              onClick={() => setPreview((p) => !p)}
              className="btn btn-ghost btn-sm"
            >
              {preview ? t('checklist.back_to_edit') : t('checklist.preview')}
            </button>
          </div>
        </div>
      </div>

      {preview && previewSchema ? (
        <div className="card">
          <ChecklistFormView schema={previewSchema} answers={{}} readOnly onChange={() => {}} />
        </div>
      ) : (
        <>
          {sections.map((section, si) => (
            <div key={section.id} className="card !p-0 overflow-hidden">
              <div className="border-l-4 border-primary bg-primary-50/40 px-5 py-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <input
                      value={section.title}
                      onChange={(e) => patchSection(section.id, { title: e.target.value })}
                      placeholder={t('checklist.section_title_placeholder') as string}
                      className="w-full bg-transparent text-[15px] font-semibold text-gray-900 outline-none"
                    />
                    <input
                      value={section.description ?? ''}
                      onChange={(e) => patchSection(section.id, { description: e.target.value })}
                      placeholder={t('checklist.section_description_placeholder') as string}
                      className="mt-1 w-full bg-transparent text-[12px] text-muted outline-none"
                    />
                  </div>
                  <span className="text-[11px] uppercase tracking-wide text-muted shrink-0 pt-1">
                    {t('checklist.section_n', { n: si + 1 })}
                  </span>
                  {sections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setSections((prev) => prev.filter((s) => s.id !== section.id))}
                      className="btn btn-ghost btn-sm text-danger shrink-0"
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4 space-y-3">
                {section.questions.map((q, qi) => (
                  <QuestionCard
                    key={q.id}
                    index={qi}
                    question={q}
                    canMoveUp={qi > 0}
                    canMoveDown={qi < section.questions.length - 1}
                    onChange={(patch) => patchQuestion(section.id, q.id, patch)}
                    onChangeType={(type) => changeType(section.id, q.id, type)}
                    onMove={(delta) => moveQuestion(section.id, qi, delta)}
                    onDuplicate={() => duplicateQuestion(section.id, q)}
                    onRemove={() => removeQuestion(section.id, q.id)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => addQuestion(section.id)}
                  className="w-full rounded-md border border-dashed border-border-strong py-2.5 text-[13px] font-medium text-muted transition-colors hover:border-primary hover:text-primary"
                >
                  ＋ {t('checklist.add_question')}
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              setSections((prev) => [
                ...prev,
                blankSection(t('checklist.section_n', { n: prev.length + 1 }) as string),
              ])
            }
            className="w-full rounded-lg border border-dashed border-border-strong py-3 text-[13px] font-medium text-muted transition-colors hover:border-primary hover:text-primary"
          >
            ＋ {t('checklist.add_section')}
          </button>
        </>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-danger-bg px-3 py-2 text-[13px] text-danger">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-ghost">
            {t('common.cancel')}
          </button>
        )}
        <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? t('common.saving') : saveLabel || t('checklist.save_and_attach')}
        </button>
      </div>
    </div>
  );
}

function QuestionCard({
  index,
  question,
  canMoveUp,
  canMoveDown,
  onChange,
  onChangeType,
  onMove,
  onDuplicate,
  onRemove,
}: {
  index: number;
  question: ChecklistQuestion;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (patch: Partial<ChecklistQuestion>) => void;
  onChangeType: (type: QuestionType) => void;
  onMove: (delta: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const showOptions = OPTION_TYPES.includes(question.type);
  const commentOptions = effectiveOptions(question);
  const fixedOptions =
    question.type === 'yes_no'
      ? ['Yes', 'No']
      : question.type === 'yes_no_na'
        ? ['Yes', 'No', 'N/A']
        : null;

  const setOption = (i: number, value: string) => {
    const next = [...(question.options ?? [])];
    next[i] = value;
    onChange({ options: next });
  };

  return (
    <div className="rounded-md border border-border bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="mt-2 w-6 shrink-0 text-right text-[12px] font-semibold text-muted">
          {index + 1}.
        </span>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={question.label}
              onChange={(e) => onChange({ label: e.target.value })}
              placeholder={t('checklist.question_placeholder') as string}
              className="input flex-1"
            />
            <select
              value={question.type}
              onChange={(e) => onChangeType(e.target.value as QuestionType)}
              className="select sm:w-52"
            >
              {TYPE_GROUPS.map((group) => (
                <optgroup key={group.key} label={t(`checklist.type_group.${group.key}`) as string}>
                  {group.types.map((type) => (
                    <option key={type} value={type}>
                      {t(`checklist.type.${type}`)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <input
            value={question.help ?? ''}
            onChange={(e) => onChange({ help: e.target.value })}
            placeholder={t('checklist.help_placeholder') as string}
            className="w-full bg-transparent text-[12px] text-muted outline-none"
          />

          {showOptions && (
            <div className="space-y-1.5 pt-1">
              {(question.options ?? []).map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-muted text-[13px]">
                    {question.type === 'multi_choice' ? '☐' : '◯'}
                  </span>
                  <input
                    value={opt}
                    onChange={(e) => setOption(i, e.target.value)}
                    placeholder={t('checklist.option_n', { n: i + 1 }) as string}
                    className="input !py-1.5 flex-1"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      onChange({ options: (question.options ?? []).filter((_, j) => j !== i) })
                    }
                    className="btn btn-ghost btn-sm text-danger"
                    title={t('common.delete') as string}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => onChange({ options: [...(question.options ?? []), ''] })}
                className="btn btn-ghost btn-sm"
              >
                ＋ {t('checklist.add_option')}
              </button>
            </div>
          )}

          {fixedOptions && (
            <div className="flex flex-wrap gap-2 pt-1">
              {fixedOptions.map((o) => (
                <span key={o} className="badge badge-muted">
                  ◯ {o}
                </span>
              ))}
            </div>
          )}

          {question.type === 'rating' && (
            <label className="flex items-center gap-2 pt-1 text-[13px] text-muted">
              {t('checklist.rating_max')}
              <select
                value={question.max ?? 5}
                onChange={(e) => onChange({ max: Number(e.target.value) })}
                className="select !w-20 !py-1"
              >
                {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Comment box: always offerable, and for choice questions the author
              picks which answers cannot be left unexplained (e.g. a No). */}
          {question.allowComment && commentOptions.length > 0 && (
            <div className="rounded-md border border-dashed border-border-strong bg-[#FAFBFC] px-3 py-2">
              <div className="text-[12px] text-muted">{t('checklist.comment_required_for')}</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {commentOptions.map((o) => {
                  const on = (question.commentRequiredFor ?? []).includes(o);
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() =>
                        onChange({
                          commentRequiredFor: on
                            ? (question.commentRequiredFor ?? []).filter((x) => x !== o)
                            : [...(question.commentRequiredFor ?? []), o],
                        })
                      }
                      className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors ${
                        on
                          ? 'border-primary bg-primary text-white'
                          : 'border-border-strong bg-white text-muted hover:border-primary'
                      }`}
                    >
                      {on ? '✓ ' : ''}
                      {o}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[11px] text-muted">{t('checklist.comment_hint')}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-1 border-t border-border pt-2">
        <div className="mr-auto flex flex-wrap items-center gap-x-4 gap-y-1">
          <label className="flex items-center gap-2 text-[13px] text-gray-900">
            <input
              type="checkbox"
              checked={question.required}
              onChange={(e) => onChange({ required: e.target.checked })}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary/20"
            />
            {t('checklist.required')}
          </label>
          <label className="flex items-center gap-2 text-[13px] text-gray-900">
            <input
              type="checkbox"
              checked={question.allowComment === true}
              onChange={(e) => onChange({ allowComment: e.target.checked })}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary/20"
            />
            {t('checklist.allow_comment')}
          </label>
        </div>
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={!canMoveUp}
          className="btn btn-ghost btn-sm disabled:opacity-30"
          title={t('checklist.move_up') as string}
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={!canMoveDown}
          className="btn btn-ghost btn-sm disabled:opacity-30"
          title={t('checklist.move_down') as string}
        >
          ↓
        </button>
        <button type="button" onClick={onDuplicate} className="btn btn-ghost btn-sm">
          {t('checklist.duplicate')}
        </button>
        <button type="button" onClick={onRemove} className="btn btn-ghost btn-sm text-danger">
          {t('common.delete')}
        </button>
      </div>
    </div>
  );
}
